import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Typography, Box } from '@mui/material';
import { UnknownAction } from '@reduxjs/toolkit';
import { useAppSelector } from '../../../../store/hooks';
import Line from '../../../../shared/components/line/index';
import { metaDataSelector } from '../../../../store/globalSelectors';
import { BMICard } from '../../../../shared/components/bmiCard/index';
import { henryOxfordSelection } from '../../../../shared/shared.constants';
import LightBlueCard from '../../../../shared/components/lightBlueCard/index';
import { ActivityFactorPALSelectType } from '../../../../shared/shared.interfaces';
import BmiIcon from '../../../../assets/icons/Icon_Improve Treatment_Large_White.svg';
import {
  ToggleButtonStyled,
  ToggleButtonGroupStyled
} from '../../../../shared/components/toggleButtons/styles';

export const PALSelect = ({
  BMRVal,
  activityFactorPAL_Select,
  setActivityFactorPAL_Select
}: {
  BMRVal: number;
  activityFactorPAL_Select: ActivityFactorPALSelectType;
  setActivityFactorPAL_Select: (
    val: ActivityFactorPALSelectType
  ) => UnknownAction;
}) => {
  const dispatch = useDispatch();
  const { patientInformation } = useAppSelector(metaDataSelector);

  const handleChangeOccupational = (event: any) => {
    dispatch(
      setActivityFactorPAL_Select({
        ...activityFactorPAL_Select,
        occupationalActivityLevel: event.currentTarget.value
      })
    );
  };

  const handleChangeNoNOccupational = (event: any) => {
    dispatch(
      setActivityFactorPAL_Select({
        ...activityFactorPAL_Select,
        nonOccupationalActivityLevel: event.currentTarget.value
      })
    );
  };

  const PALSelect = React.useMemo(() => {
    if (
      activityFactorPAL_Select.occupationalActivityLevel &&
      activityFactorPAL_Select.nonOccupationalActivityLevel
    )
      return Number(
        (
          (henryOxfordSelection.pal.select[
            patientInformation.gender.toLowerCase()
          ][activityFactorPAL_Select.occupationalActivityLevel][
            activityFactorPAL_Select.nonOccupationalActivityLevel
          ] -
            1) *
          BMRVal
        ).toFixed(2)
      );
    else return 0;
  }, [BMRVal, activityFactorPAL_Select, henryOxfordSelection]);

  useEffect(() => {
    const bmrPALValue = Number((BMRVal + PALSelect).toFixed(2));
    dispatch(
      setActivityFactorPAL_Select({
        ...activityFactorPAL_Select,
        bmrPlusPALSelect: bmrPALValue
      })
    );
  }, [BMRVal, PALSelect]);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '1.875rem',
        flexDirection: 'column',
        marginTop: '1.875rem'
      }}
    >
      <Typography
        sx={{ fontWeight: 400, fontSize: '1rem', lineHeight: '1.25rem' }}
      >
        Occupational Activity Level
      </Typography>
      <ToggleButtonGroupStyled
        color="primary"
        value={activityFactorPAL_Select.occupationalActivityLevel}
        exclusive
        onChange={handleChangeOccupational}
        aria-label="Platform"
        sx={{ padding: '0px', justifyContent: 'space-between' }}
      >
        <ToggleButtonStyled
          sx={{
            width: '30%',
            padding: '0.625rem 0.3125rem',
            lineHeight: '1.25rem',
            fontWeight: 400
          }}
          value="light"
        >
          Light
        </ToggleButtonStyled>
        <ToggleButtonStyled
          sx={{
            width: '30%',
            padding: '0.625rem 0.3125rem',
            lineHeight: '1.25rem',
            fontWeight: 400
          }}
          value="moderate"
        >
          Moderate
        </ToggleButtonStyled>
        <ToggleButtonStyled
          sx={{
            width: '30%',
            padding: '0.625rem 0.3125rem',
            lineHeight: '1.25rem',
            fontWeight: 400
          }}
          value="heavy"
        >
          Heavy
        </ToggleButtonStyled>
      </ToggleButtonGroupStyled>
      <Line color="#D9D9D6" width="100%" height="0.0625rem" />
      <Typography
        sx={{ fontWeight: 400, fontSize: '1rem', lineHeight: '1.25rem' }}
      >
        Non-occupational Activity Level
      </Typography>
      <ToggleButtonGroupStyled
        color="primary"
        value={activityFactorPAL_Select.nonOccupationalActivityLevel}
        exclusive
        onChange={handleChangeNoNOccupational}
        aria-label="Platform"
        sx={{ padding: '0px', justifyContent: 'space-between' }}
      >
        <ToggleButtonStyled
          sx={{
            width: '30%',
            padding: '0.625rem 0.3125rem',
            lineHeight: '1.25rem',
            fontWeight: 400
          }}
          value="nonActive"
        >
          Non Active
        </ToggleButtonStyled>
        <ToggleButtonStyled
          sx={{
            width: '30%',
            padding: '0.625rem 0.3125rem',
            lineHeight: '1.25rem',
            fontWeight: 400
          }}
          value="active"
        >
          Moderately Active
        </ToggleButtonStyled>
        <ToggleButtonStyled
          sx={{
            width: '30%',
            padding: '0.625rem 0.3125rem',
            lineHeight: '1.25rem',
            fontWeight: 400
          }}
          value="veryActive"
        >
          Very Active
        </ToggleButtonStyled>
      </ToggleButtonGroupStyled>
      <Line color="#D9D9D6" width="100%" height="0.0625rem" />
      <LightBlueCard label={`PAL (kcal):`} value={`+ ${PALSelect}`} />
      <BMICard
        label1="BMR + PAL"
        label2={`${activityFactorPAL_Select.bmrPlusPALSelect} kcal`}
        icon={BmiIcon}
      />
    </Box>
  );
};
